import { Injectable, computed, signal } from '@angular/core';
import type {
  RecurringTaskTemplate,
  RecurringTaskExecution,
  RecurringTaskSchedule
} from '@shared/definitions/recurring-task.model';
import { TaskStatus, type TaskCard } from '../../../../shared/definitions/task-card.model';
import type { IRecurringTasksStore } from './recurring-tasks-store.interface';

@Injectable({ providedIn: 'root' })
export class RecurringTasksMockStore implements IRecurringTasksStore {
  private readonly _templates = signal<readonly RecurringTaskTemplate[]>([]);
  private readonly _executions = signal<readonly RecurringTaskExecution[]>([]);
  private readonly _schedules = signal<readonly RecurringTaskSchedule[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly templates = this._templates.asReadonly();
  readonly executions = this._executions.asReadonly();
  readonly schedules = this._schedules.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly activeTemplates = computed(() => this._templates().filter(t => t.isActive));
  readonly totalTemplates = computed(() => this._templates().length);

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  async createTemplate(
    template: Omit<RecurringTaskTemplate, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<RecurringTaskTemplate> {
    const now = new Date();
    const created: RecurringTaskTemplate = {
      ...template,
      id: `mock-tmpl-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      createdAt: now,
      updatedAt: now
    };
    this._templates.set([created, ...this._templates()]);
    return created;
  }

  async updateTemplate(templateId: string, updates: Partial<RecurringTaskTemplate>): Promise<void> {
    this._templates.set(
      this._templates().map(t =>
        t.id === templateId ? { ...t, ...updates, updatedAt: new Date() } : t
      )
    );
  }

  async deleteTemplate(templateId: string): Promise<void> {
    this._templates.set(this._templates().filter(t => t.id !== templateId));
  }

  getTemplateById(templateId: string): RecurringTaskTemplate | null {
    return this._templates().find(t => t.id === templateId) ?? null;
  }

  getTemplatesForProperty(propertyUID: string): readonly RecurringTaskTemplate[] {
    return this._templates().filter(t => t.propertyUIDs?.includes(propertyUID));
  }

  getDueExecutions(): readonly RecurringTaskSchedule[] {
    const now = Date.now();
    return this._schedules().filter(s => new Date(s.nextExecutionDate).getTime() <= now);
  }

  getUpcomingExecutions(daysAhead = 30): readonly RecurringTaskSchedule[] {
    const now = Date.now();
    const cutoff = now + daysAhead * 86_400_000;
    return this._schedules().filter(s => {
      const t = new Date(s.nextExecutionDate).getTime();
      return t > now && t <= cutoff;
    });
  }

  async createTaskFromTemplate(
    template: RecurringTaskTemplate,
    propertyUID: string,
    scheduledDate: Date,
    propertyName?: string
  ): Promise<TaskCard> {
    const id = `mock-task-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    return {
      id,
      title: `${template.name} - ${propertyName || 'Property'}`,
      description: template.description,
      type: template.type,
      status: TaskStatus.PLANNED,
      priority: template.priority,
      propertyUID,
      propertyName: propertyName || '',
      propertyAddress: '',
      scheduledDate,
      dueDate: scheduledDate,
      createdAt: new Date(),
      updatedAt: new Date()
    } as unknown as TaskCard;
  }

  async skipExecution(executionId: string, reason: string): Promise<void> {
    this._executions.set(
      this._executions().map(e =>
        e.id === executionId ? { ...e, status: 'skipped', skipReason: reason } : e
      )
    );
  }

  async getTemplate(templateId: string): Promise<RecurringTaskTemplate | undefined> {
    return this._templates().find(t => t.id === templateId);
  }
}
